import React from "react"
import ReactTooltip from "react-tooltip"
import {ManagedSolutionsUser} from "../../resources/managedSolutions"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import classNames from "classnames"
import {startCase} from "lodash"
import moment from "moment"
import {Meta} from "../../resources/common"
import {Link, useNavigate} from "react-router-dom"
import {FunnelStage, deriveFunnelStage} from "./funnelStage"

interface ManagedSolutionsUsersTableProps {
	users: Array<ManagedSolutionsUser>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	fullHeight?: boolean
	meta?: Meta
	sortBy?: string
	sortFunction?: () => void
}

const FUNNEL_TOOLTIP_ID = "managed-solutions-funnel-stage-tooltip"

function funnelTooltipContent(user: ManagedSolutionsUser, stage: FunnelStage): string {
	const {applicationFormStage, applicationStatus} = user.attributes

	switch (stage.label) {
		case "Invited":
			return "User was invited but hasn't started their application form yet."
		case "Onboarding": {
			const step = applicationFormStage ? ` Currently at: ${startCase(applicationFormStage)}.` : ""
			return `User is filling out their application form.${step}`
		}
		case "In Review": {
			const status = applicationStatus ? ` Status: ${startCase(applicationStatus)}.` : ""
			return `Application has been submitted and is under review.${status}`
		}
		case "Approved":
			return "Application approved — waiting for account activation."
		case "Activated":
			return "Fully onboarded with an active customer."
		case "Denied":
			return "Application was reviewed and denied."
		default:
			return stage.label
	}
}

function FunnelStagePill({stage, user}: {stage: FunnelStage; user: ManagedSolutionsUser}) {
	return (
		<div
			className={classNames("managed-solutions-user-funnel-stage", stage.tone)}
			data-tip={funnelTooltipContent(user, stage)}
			data-for={FUNNEL_TOOLTIP_ID}
		>
			<span className="managed-solutions-user-funnel-stage-label">{stage.label}</span>
		</div>
	)
}

interface FunnelStepProps {
	label: string
	id?: string
	to?: string
}

function FunnelStep({label, id, to}: FunnelStepProps) {
	const active = Boolean(id)
	const inner = (
		<>
			<span className="managed-solutions-funnel-step-check">{active ? "✓" : ""}</span>
			<span className="managed-solutions-funnel-step-label">{label}</span>
		</>
	)
	return active && to ? (
		<Link className="managed-solutions-funnel-step active" to={to} onClick={(e) => e.stopPropagation()}>
			{inner}
		</Link>
	) : (
		<span className={`managed-solutions-funnel-step ${active ? "active" : "empty"}`}>{inner}</span>
	)
}

function ActivationFunnelCell({user}: {user: ManagedSolutionsUser}) {
	const applicationFormId = user.relationships?.applicationForm?.data?.id
	const applicationId = user.relationships?.application?.data?.id
	const customerId = user.relationships?.customer?.data?.id

	return (
		<div className="managed-solutions-activation-funnel">
			<FunnelStep
				label="Application Form"
				id={applicationFormId}
				to={applicationFormId ? `/managed-solutions/application-forms/${applicationFormId}` : undefined}
			/>
			<span className="managed-solutions-funnel-connector" aria-hidden="true" />
			<FunnelStep
				label="Application Submitted"
				id={applicationId}
				to={applicationId ? `/managed-solutions/applications/${applicationId}` : undefined}
			/>
			<span className="managed-solutions-funnel-connector" aria-hidden="true" />
			<FunnelStep
				label="Customer"
				id={customerId}
				to={customerId ? `/managed-solutions/customers/${customerId}` : undefined}
			/>
		</div>
	)
}

export default function ManagedSolutionsUsersTable({
	users,
	hasPrev,
	hasNext,
	prev,
	next,
	hasResults,
	isUsingPaging,
	fullHeight,
	meta,
	sortBy,
	sortFunction,
}: ManagedSolutionsUsersTableProps) {
	const noContent = users.length === 0
	const navigate = useNavigate()

	return (
		<div className={"managed-solutions-users-table"}>
			<DataTable isEmpty={noContent} fullHeight={fullHeight} noContentText="No users yet">
				<DataTableHead className={classNames(!fullHeight && "no-sticky")}>
					<DataTableRow>
						<DataTableCell>Unit ID</DataTableCell>
						<DataTableCell>Partner User ID</DataTableCell>
						<DataTableCell>Role</DataTableCell>
						<DataTableCell>Activation Funnel Stage</DataTableCell>
						<DataTableCell>Activation Funnel</DataTableCell>
						<DataTableCell clickable onClick={() => sortFunction?.()} sortable sortApplied={sortBy === "createdAt"}>
							Created At
						</DataTableCell>
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{users.map((user) => {
						const {userId, userRole, createdAt} = user.attributes
						const funnelStage = deriveFunnelStage(user)

						return (
							<DataTableRow
								key={user.id}
								onClick={(e) => {
									e.preventDefault()
									navigate(`/managed-solutions/users/${user.id}`)
								}}
							>
								<DataTableCell className="data-table-id-cell">{user.id}</DataTableCell>
								<DataTableCell>{userId}</DataTableCell>
								<DataTableCell>{startCase(userRole)}</DataTableCell>
								<DataTableCell>
									<FunnelStagePill stage={funnelStage} user={user} />
								</DataTableCell>
								<DataTableCell>
									<ActivationFunnelCell user={user} />
								</DataTableCell>
								<DataTableCell>{createdAt ? moment(createdAt).format("L LT") : "-"}</DataTableCell>
							</DataTableRow>
						)
					})}
				</DataTableBody>
			</DataTable>
			<ReactTooltip id={FUNNEL_TOOLTIP_ID} type="dark" effect="solid" className="managed-solutions-funnel-tooltip" />
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>
		</div>
	)
}
